/**
 * Ported from the lekterion codebase (`src/auth/context.ts`) @ `d942f2f`.
 * Bindery is canonical for this module from 2026-09-06; reconcile source-side
 * drift toward this copy.
 *
 * `startSignIn` reaches above the workspace's `BROWSER_FLOOR`, and the port
 * keeps it there rather than quietly repairing it. `crypto.randomUUID()` is
 * Safari 15.4 / Chrome 92 / Firefox 95, while `packages/config/vite.ts` puts
 * the floor at safari14 — so `oxlint`'s `compat/compat` warns here, and on a
 * floor browser sign-in throws instead of redirecting. Reading is unaffected:
 * everything below §5 is Tier-1 and needs no session. A fallback nonce is a
 * one-line change, but it is a CHANGE to a security-relevant path that the
 * source repo is still shipping, and this file's job today is to be the same
 * code — see the drift contract in packages/convex-lekterion/README.md.
 */
import { createContext, useCallback, useContext } from "react";

import { decodeJwt, isExpired } from "@/auth/jwt";

/**
 * Light-auth context + hooks (Phase 8), kept separate from the provider component
 * so the provider file can export only a component (fast-refresh friendly).
 *
 * The JWT issued by the broker (auth.sara.ai) lives in localStorage and is handed
 * to Convex via `useConvexLightAuth` (the `ConvexProviderWithAuth` adapter).
 * Convex validates it on every request; decoded claims here only drive the UI.
 * Two sign-in lanes — the public reader lane (Google) and the admin lane (WorkOS,
 * reached from `/admin`) — both land on `/auth/callback?code=…`.
 */

export const AUTH_BASE_URL = "https://auth.sara.ai";
export const STORAGE_KEY = "lekterion.authToken";
/** Where to send the reader after sign-in completes (set just before redirect). */
export const POST_SIGN_IN_KEY = "lekterion.postSignIn";
/** Per-attempt CSRF nonce: stashed at startSignIn, re-checked at the callback. */
export const SIGN_IN_STATE_KEY = "lekterion.signInState";

export interface LightAuthValue {
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  nickname: string | null;
  /** Start the public reader sign-in (Google lane). */
  signIn: () => void;
  /** Start the admin sign-in (WorkOS lane). */
  adminSignIn: () => void;
  signOut: () => void;
  /**
   * Drop a token the server rejected as invalid (see `isUnauthenticatedError`).
   * Unlike `signOut` this is a self-heal for a stale-but-unexpired token: clearing
   * it flips the UI to signed-out so the reader signs in again, instead of leaving a
   * dead session that keeps failing every gated call.
   */
  clearStaleSession: () => void;
  /** Store (or clear) the session JWT — used by the auth callback. */
  setToken: (token: string | null) => void;
}

export const AuthContext = createContext<LightAuthValue | null>(null);

/** Read the stored token, discarding it if absent/expired. */
export function loadToken(): string | null {
  const token = localStorage.getItem(STORAGE_KEY);
  if (!token) return null;
  if (isExpired(decodeJwt(token))) {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
  return token;
}

/** Redirect to the broker to begin a sign-in (reader = Google, admin = WorkOS). */
export function startSignIn(kind: "reader" | "admin"): void {
  // Bind this sign-in to this browser: a random nonce rides in returnTo (the
  // broker preserves returnTo's query params and only appends ?code=), and the
  // callback rejects any code whose returned nonce doesn't match what we stored.
  // Without it, an attacker's own valid code delivered to a victim would sign the
  // victim into the attacker's account (sign-in CSRF / session fixation).
  const state = crypto.randomUUID();
  sessionStorage.setItem(SIGN_IN_STATE_KEY, state);
  const returnUrl = new URL(`${window.location.origin}/auth/callback`);
  returnUrl.searchParams.set("s", state);
  const returnTo = returnUrl.toString();
  // Return the reader to wherever they were after the round-trip.
  sessionStorage.setItem(
    POST_SIGN_IN_KEY,
    window.location.pathname + window.location.search,
  );
  const url =
    kind === "admin"
      ? `${AUTH_BASE_URL}/redirect?autoSignIn=true&returnTo=${encodeURIComponent(returnTo)}`
      : `${AUTH_BASE_URL}/light/google/start?returnTo=${encodeURIComponent(returnTo)}`;
  window.location.assign(url);
}

export const useLightAuth = (): LightAuthValue => {
  const value = useContext(AuthContext);
  if (!value)
    throw new Error("useLightAuth must be used within <AuthProvider>");
  return value;
};

/**
 * Adapter for `ConvexProviderWithAuth`. Returns the stored JWT (or null when
 * absent/expired) — there is no refresh endpoint, so an expired token simply
 * reads as signed-out and the reader signs in again. Must run inside `<AuthProvider>`.
 */
export const useConvexLightAuth = (): {
  isLoading: boolean;
  isAuthenticated: boolean;
  fetchAccessToken: (args: {
    forceRefreshToken: boolean;
  }) => Promise<string | null>;
} => {
  const { token } = useLightAuth();

  const fetchAccessToken = useCallback((): Promise<string | null> => {
    if (!token) return Promise.resolve(null);
    return Promise.resolve(isExpired(decodeJwt(token)) ? null : token);
  }, [token]);

  // Expiry-aware so it never disagrees with `fetchAccessToken`: an expired token
  // reads as signed-out here just as it returns null there.
  const isAuthenticated = token !== null && !isExpired(decodeJwt(token));
  return { isLoading: false, isAuthenticated, fetchAccessToken };
};
